import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Metrics, Stats, Alert } from '../types';
import { DateFilter, DateRange } from '../components/DateFilter';
import { AutoscalingMonitor } from '../components/AutoscalingMonitor';
import {
  AlertTriangle,
  Shield,
  Users,
  FileText,
  TrendingUp,
  Clock,
  RefreshCw,
  Loader2,
  Activity,
  Database,
  Zap,
  Eye,
  Target,
  Search,
  Globe,
  BarChart3,
  PieChart as PieChartIcon,
  TrendingDown,
  Calendar,
  Server,
  Brain,
  Filter,
  Star,
  Hash,
  Link,
  ChevronRight
} from 'lucide-react';
import { formatSafeRelativeTime, formatAbsoluteTime } from '../utils/date-formatter';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  Area,
  AreaChart,
  ComposedChart,
  ReferenceLine,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const THREAT_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  unknown: '#6b7280'
};

export function Dashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
    period: 'all'
  });

  useEffect(() => {
    console.log('Dashboard effect triggered with dateRange:', dateRange);
    loadDashboardData();

    // Refresh data every 30 seconds
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, [dateRange]);

  const loadDashboardData = async () => {
    try {
      // Prepare date parameters
      const dateParams: any = {};
      if (dateRange.startDate) {
        dateParams.from_date = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        dateParams.to_date = dateRange.endDate.toISOString();
      }

      // Calculate stats range based on date selection
      let statsRange = '24h';
      if (dateRange.period === '7days') {
        statsRange = '7d';
      } else if (dateRange.period === '30days') {
        statsRange = '30d';
      } else if (dateRange.period === 'all') {
        statsRange = 'all';
      }

      // For "all time", don't send date parameters
      const apiDateParams = dateRange.period === 'all' ? {} : dateParams;

      console.log('Loading dashboard data with:', {
        period: dateRange.period,
        statsRange,
        apiDateParams,
        dateRange
      });

      const [metricsData, statsData, alertsData] = await Promise.all([
        api.getMetrics(apiDateParams),
        api.getStats(statsRange, apiDateParams),
        api.getAlerts({ limit: 5, acknowledged: false, ...apiDateParams })
      ]);

      // Parse metrics to ensure numbers
      const parsedMetrics = {
        ...metricsData,
        processing_stats: metricsData.processing_stats ? {
          ...metricsData.processing_stats,
          total_posts: typeof metricsData.processing_stats.total_posts === 'string' ? parseInt(metricsData.processing_stats.total_posts, 10) : metricsData.processing_stats.total_posts,
          processed_posts: typeof metricsData.processing_stats.processed_posts === 'string' ? parseInt(metricsData.processing_stats.processed_posts, 10) : metricsData.processing_stats.processed_posts,
          analyzed_posts: typeof metricsData.processing_stats.analyzed_posts === 'string' ? parseInt(metricsData.processing_stats.analyzed_posts, 10) : metricsData.processing_stats.analyzed_posts,
          pending_analysis: typeof metricsData.processing_stats.pending_analysis === 'string' ? parseInt(metricsData.processing_stats.pending_analysis, 10) : metricsData.processing_stats.pending_analysis
        } : metricsData.processing_stats,
        threat_distribution: metricsData.threat_distribution ? metricsData.threat_distribution.map((item: any) => ({
          ...item,
          count: typeof item.count === 'string' ? parseInt(item.count, 10) : item.count
        })) : []
      };

      // Parse stats data to ensure numbers
      const parsedStats = {
        ...statsData,
        stats: statsData.stats ? {
          ...statsData.stats,
          total_posts: typeof statsData.stats.total_posts === 'string' ? parseInt(statsData.stats.total_posts, 10) : statsData.stats.total_posts,
          high_threats: typeof statsData.stats.high_threats === 'string' ? parseInt(statsData.stats.high_threats, 10) : statsData.stats.high_threats,
          critical_threats: typeof statsData.stats.critical_threats === 'string' ? parseInt(statsData.stats.critical_threats, 10) : statsData.stats.critical_threats,
          medium_threats: typeof statsData.stats.medium_threats === 'string' ? parseInt(statsData.stats.medium_threats, 10) : statsData.stats.medium_threats,
          low_threats: typeof statsData.stats.low_threats === 'string' ? parseInt(statsData.stats.low_threats, 10) : statsData.stats.low_threats,
          unique_users: typeof statsData.stats.unique_users === 'string' ? parseInt(statsData.stats.unique_users, 10) : statsData.stats.unique_users,
          analyzed_posts: typeof statsData.stats.analyzed_posts === 'string' ? parseInt(statsData.stats.analyzed_posts, 10) : statsData.stats.analyzed_posts,
          unanalyzed_posts: typeof statsData.stats.unanalyzed_posts === 'string' ? parseInt(statsData.stats.unanalyzed_posts, 10) : statsData.stats.unanalyzed_posts,
        } : statsData.stats,
        trend_data: statsData.trend_data ? statsData.trend_data.map((item: any) => ({
          ...item,
          post_count: typeof item.post_count === 'string' ? parseInt(item.post_count, 10) : item.post_count,
          threat_count: typeof item.threat_count === 'string' ? parseInt(item.threat_count, 10) : item.threat_count
        })) : []
      };

      setMetrics(parsedMetrics);
      setStats(parsedStats);
      setRecentAlerts(alertsData.alerts);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load dashboard data. Please check if the backend API is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null); // Clear previous errors
    await loadDashboardData();
    setRefreshing(false);
  };

  const triggerIngestion = async () => {
    try {
      setIngesting(true);

      // Trigger both regular RSS and RSS.app ingestion
      const [rssResult, rssAppResult] = await Promise.allSettled([
        api.triggerIngestion('all'),
        api.triggerRSSAppIngestion()
      ]);

      if (rssResult.status === 'fulfilled') {
        console.log('RSS ingestion triggered:', rssResult.value);
      }
      if (rssAppResult.status === 'fulfilled') {
        console.log('RSS.app ingestion triggered:', rssAppResult.value);
      }

      // Wait a bit then refresh data
      setTimeout(async () => {
        await loadDashboardData();
        setIngesting(false);
      }, 5000);
    } catch (error) {
      console.error('Failed to trigger ingestion:', error);
      setIngesting(false);
    }
  };

  // Calculate hasData early to avoid TDZ error
  const rawTotalPosts = metrics?.processing_stats?.total_posts;
  const totalPosts = typeof rawTotalPosts === 'string' ? parseInt(rawTotalPosts, 10) : (rawTotalPosts || 0);
  const hasData = totalPosts > 0;

  console.log('[DEBUG] Dashboard Render:', {
    metrics,
    rawTotalPosts,
    totalPosts,
    hasData,
    typeOfRaw: typeof rawTotalPosts
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show error state if API calls failed
  if (error && !hasData) {
    return (
      <div className="space-y-6">
        {/* Header with actions */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <div className="flex space-x-4">
            <DateFilter
              onChange={setDateRange}
              initialValue={dateRange}
            />
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              aria-label="Refresh dashboard data"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
              Refresh
            </button>
          </div>
        </div>

        {/* Error State */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" aria-hidden="true" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-red-800 mb-2">API Connection Error</h3>
              <p className="text-red-700 mb-4">{error}</p>
              <div className="text-sm text-red-600">
                <p className="mb-2"><strong>Possible causes:</strong></p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Backend API worker is not deployed</li>
                  <li>Service binding configuration is missing</li>
                  <li>Network connectivity issues</li>
                  <li>API endpoints are returning 500 errors</li>
                </ul>
                <p className="mt-4"><strong>Solution:</strong> Try refreshing, or contact system administrator if the problem persists.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Group and aggregate threat distribution data by level - simple approach to avoid useMemo infinite loop
  let threatDistribution: Array<{ name: string; value: number; color: string }> = [];

  if (metrics?.threat_distribution) {
    // Group by normalized threat level
    const grouped = metrics.threat_distribution.reduce((acc, item) => {
      const threatLevel = item.threat_level.toLowerCase();
      let levelName = 'unknown';

      if (threatLevel.includes('critical')) {
        levelName = 'critical';
      } else if (threatLevel.includes('high')) {
        levelName = 'high';
      } else if (threatLevel.includes('medium')) {
        levelName = 'medium';
      } else if (threatLevel.includes('low')) {
        levelName = 'low';
      }

      if (!acc[levelName]) {
        acc[levelName] = 0;
      }
      acc[levelName] += item.count;

      return acc;
    }, {} as Record<string, number>);

    // Convert to array format for the pie chart
    threatDistribution = Object.entries(grouped).map(([level, count]) => ({
      name: level,
      value: count,
      color: THREAT_COLORS[level as keyof typeof THREAT_COLORS] || THREAT_COLORS.unknown
    }));
  }

  // Generate dynamic subtitle based on selected date range
  const getDateRangeLabel = () => {
    switch (dateRange.period) {
      case '7days':
        return 'Posts from last 7 days';
      case '30days':
        return 'Posts from last 30 days';
      case 'custom':
        if (dateRange.startDate && dateRange.endDate) {
          const start = dateRange.startDate.toLocaleDateString();
          const end = dateRange.endDate.toLocaleDateString();
          return `Posts from ${start} to ${end}`;
        }
        return 'Custom date range';
      case 'all':
      default:
        return 'All-time posts ingested';
    }
  };

  const getThreatRangeLabel = () => {
    switch (dateRange.period) {
      case '7days':
        return 'Last 7 days';
      case '30days':
        return 'Last 30 days';
      case 'custom':
        return 'Custom period';
      case 'all':
      default:
        return 'All time period';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex space-x-4">
          <DateFilter
            onChange={setDateRange}
            initialValue={dateRange}
          />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            aria-label="Refresh dashboard data"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
          <button
            onClick={triggerIngestion}
            disabled={ingesting}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            aria-label={ingesting ? "Ingesting RSS feeds in progress" : "Start RSS feed ingestion"}
          >
            {ingesting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                Ingesting...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
                Ingest RSS Feeds
              </>
            )}
          </button>
        </div>
      </div>

      {!hasData ? (
        // Empty state
        <div className="bg-white shadow rounded-lg p-8">
          <div className="text-center">
            <Shield className="mx-auto h-12 w-12 text-gray-400" aria-hidden="true" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No data yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by ingesting RSS feeds to populate your threat intelligence dashboard.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={triggerIngestion}
                disabled={ingesting}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                aria-label={ingesting ? "RSS feed ingestion in progress" : "Start RSS feed ingestion"}
              >
                {ingesting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                    Ingesting RSS Feeds...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
                    Start Ingesting RSS Feeds
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Error Banner (if error but some data is available) */}
          {error && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" aria-hidden="true" />
                <div className="flex-1">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> Some API calls failed. Data may be incomplete.
                    <button
                      onClick={handleRefresh}
                      className="underline hover:no-underline ml-1"
                    >
                      Try refreshing
                    </button>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Primary Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Posts"
              value={metrics?.processing_stats?.total_posts || 0}
              icon={FileText}
              color="blue"
              subtitle={getDateRangeLabel()}
              trend={metrics?.processing_stats?.processed_posts ? {
                value: Math.round((metrics.processing_stats.analyzed_posts / metrics.processing_stats.processed_posts) * 100),
                label: "% analyzed"
              } : undefined}
            />
            <StatCard
              title="Critical Threats"
              value={stats?.stats?.critical_threats || 0}
              icon={AlertTriangle}
              color="red"
              subtitle={`Critical threats - ${getThreatRangeLabel()}`}
              trend={{
                value: (stats?.stats?.critical_threats || 0) > 0 ? 'HIGH' : 'LOW',
                label: "alert level"
              }}
            />
            <StatCard
              title="High Threats"
              value={stats?.stats?.high_threats || 0}
              icon={Shield}
              color="orange"
              subtitle="Active monitoring needed"
            />
            <StatCard
              title="Active Users"
              value={stats?.stats?.unique_users || 0}
              icon={Users}
              color="green"
              subtitle="Contributors in dataset"
            />
          </div>

          {/* Secondary Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            <MiniStatCard
              title="Processed"
              value={metrics?.processing_stats?.processed_posts || 0}
              icon={Database}
              color="blue"
            />
            <MiniStatCard
              title={`In Queue${metrics?.processing_stats?.queue_filtering_active ? ` (${metrics?.processing_stats?.threat_threshold})` : ''}`}
              value={metrics?.processing_stats?.pending_analysis || 0}
              icon={Clock}
              color="emerald"
              subtitle={metrics?.processing_stats?.total_eligible_posts ? `${metrics.processing_stats.total_eligible_posts} total eligible` : undefined}
            />
            <MiniStatCard
              title="Analyzed"
              value={metrics?.processing_stats?.analyzed_posts || 0}
              icon={Brain}
              color="purple"
            />
            <MiniStatCard
              title="Medium Threats"
              value={stats?.stats?.medium_threats || 0}
              icon={Eye}
              color="yellow"
            />
            <MiniStatCard
              title="Low Threats"
              value={stats?.stats?.low_threats || 0}
              icon={Activity}
              color="green"
            />
            <MiniStatCard
              title="RSS Sources"
              value={metrics?.feed_stats?.total_feeds || 0}
              icon={Globe}
              color="indigo"
            />
            <MiniStatCard
              title="Processing Rate"
              value={metrics?.processing_stats?.processed_posts ?
                Math.round((metrics.processing_stats.analyzed_posts / metrics.processing_stats.processed_posts) * 100) : 0}
              icon={Zap}
              color="emerald"
              suffix="%"
            />
          </div>

          {/* Enhanced Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Threat Trend with Area Chart */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Threat Activity Trend</h3>
                  <p className="text-sm text-gray-500">Posts and threats over time</p>
                </div>
                <BarChart3 className="h-6 w-6 text-blue-500" aria-hidden="true" />
              </div>
              <div style={{ width: '100%', height: 350 }}>
                <ResponsiveContainer>
                  <ComposedChart data={stats?.trend_data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="time_bucket"
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <Tooltip
                      labelFormatter={(value) => new Date(value).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      contentStyle={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="post_count"
                      fill="#dbeafe"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      name="Total Posts"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="threat_count"
                      fill="#ef4444"
                      name="Threats"
                      radius={[2, 2, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="threat_count"
                      stroke="#dc2626"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      name="Threat Trend"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Enhanced Threat Distribution */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Threat Level Distribution</h3>
                  <p className="text-sm text-gray-500">Breakdown by severity level</p>
                </div>
                <PieChartIcon className="h-6 w-6 text-purple-500" aria-hidden="true" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div style={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={threatDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {threatDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          `${value} threats`,
                          name.charAt(0).toUpperCase() + name.slice(1)
                        ]}
                        contentStyle={{
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  {threatDistribution.map((entry, index) => {
                    const total = threatDistribution.reduce((a, b) => a + b.value, 0);
                    const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="font-medium text-sm capitalize text-gray-700">
                            {entry.name}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-gray-900">{entry.value}</div>
                          <div className="text-xs text-gray-500">{percentage}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Analytics Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Hourly Activity Heatmap */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Daily Activity</h3>
                  <p className="text-sm text-gray-500">Posts by hour</p>
                </div>
                <Clock className="h-5 w-5 text-green-500" aria-hidden="true" />
              </div>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={generateHourlyData(stats?.trend_data || [])} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="hour"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => `${value}:00`}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip
                      labelFormatter={(value) => `${value}:00`}
                      formatter={(value: number) => [value, 'Posts']}
                    />
                    <Bar
                      dataKey="count"
                      fill="#10b981"
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Processing Efficiency */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Processing Pipeline</h3>
                  <p className="text-sm text-gray-500">System efficiency</p>
                </div>
                <Target className="h-5 w-5 text-indigo-500" aria-hidden="true" />
              </div>
              <div className="space-y-4">
                <ProcessingStep
                  label="Ingested"
                  value={metrics?.processing_stats?.total_posts || 0}
                  percentage={100}
                  color="#3b82f6"
                />
                <ProcessingStep
                  label="Processed"
                  value={metrics?.processing_stats?.processed_posts || 0}
                  percentage={metrics?.processing_stats?.total_posts ?
                    (metrics.processing_stats.processed_posts / metrics.processing_stats.total_posts) * 100 : 0}
                  color="#8b5cf6"
                />
                <ProcessingStep
                  label={`In Queue${metrics?.processing_stats?.queue_filtering_active ? ` (${metrics?.processing_stats?.threat_threshold})` : ''}`}
                  value={metrics?.processing_stats?.pending_analysis || 0}
                  percentage={metrics?.processing_stats?.total_posts ?
                    ((metrics?.processing_stats?.pending_analysis || 0) / metrics.processing_stats.total_posts) * 100 : 0}
                  color="#f59e0b"
                  subtitle={metrics?.processing_stats?.total_eligible_posts ? `${metrics.processing_stats.total_eligible_posts} total eligible` : undefined}
                />
                <ProcessingStep
                  label="Analyzed"
                  value={metrics?.processing_stats?.analyzed_posts || 0}
                  percentage={metrics?.processing_stats?.total_posts ?
                    (metrics.processing_stats.analyzed_posts / metrics.processing_stats.total_posts) * 100 : 0}
                  color="#10b981"
                />
                {metrics?.processing_stats?.cloudflare_queue_size !== undefined && (
                  <ProcessingStep
                    label="Cloudflare Queue"
                    value={metrics.processing_stats.cloudflare_queue_size}
                    percentage={0}
                    color="#6366f1"
                    subtitle="Messages in CF Queue"
                  />
                )}
              </div>
            </div>

            {/* Queue Autoscaling Monitor */}
            <div className="w-full h-full">
              <AutoscalingMonitor className="shadow-lg w-full h-full" compact={true} />
            </div>
          </div>

          {/* Quick Stats Summary - Separate row */}
          <div className="bg-white p-6 rounded-lg shadow-lg border mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Quick Insights</h3>
                <p className="text-sm text-gray-500">Key metrics at a glance</p>
              </div>
              <Star className="h-5 w-5 text-yellow-500" aria-hidden="true" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <QuickStat
                label="Avg. Processing Time"
                value="2.3s"
                icon={Clock}
                color="blue"
              />
              <QuickStat
                label="Detection Rate"
                value={`${Math.round(((stats?.stats?.critical_threats || 0) + (stats?.stats?.high_threats || 0)) /
                  Math.max(metrics?.processing_stats?.analyzed_posts || 1, 1) * 100)}%`}
                icon={Eye}
                color="red"
              />
              <QuickStat
                label="Active Feeds"
                value={metrics?.feed_stats?.active_feeds || 0}
                icon={Globe}
                color="green"
              />
              <QuickStat
                label="Data Quality"
                value="94%"
                icon={Database}
                color="purple"
              />
            </div>
          </div>

          {/* Recent Alerts */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Recent Alerts</h3>
                <Clock className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {recentAlerts.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  No recent alerts
                </div>
              ) : (
                recentAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                    onClick={() => navigate(`/alerts?highlight=${alert.id}`)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`
                        flex-shrink-0 w-2 h-2 mt-2 rounded-full
                        ${alert.severity === 'critical' ? 'bg-red-500' :
                          alert.severity === 'high' ? 'bg-orange-500' :
                            alert.severity === 'medium' ? 'bg-yellow-500' :
                              'bg-green-500'}
                      `} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {alert.message}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatAbsoluteTime(alert.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`
                          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                              alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'}
                        `}>
                          {alert.severity.toUpperCase()}
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Top Users */}
          {metrics?.top_users && metrics.top_users.length > 0 && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Most Active Users</h3>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-3">
                  {metrics.top_users.slice(0, 5).map((user, index) => (
                    <div key={user.username} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-800">
                              {index + 1}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            @{user.username}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.post_count} posts
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Helper functions
function generateHourlyData(trendData: any[]) {
  const hourlyStats = new Array(24).fill(0).map((_, i) => ({ hour: i, count: 0 }));

  trendData.forEach(item => {
    const hour = new Date(item.time_bucket).getHours();
    hourlyStats[hour].count += item.post_count || 0;
  });

  return hourlyStats;
}

// Component interfaces and implementations
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'red' | 'orange' | 'green';
  subtitle?: string;
  trend?: {
    value: number | string;
    label: string;
  };
}

function StatCard({ title, value, icon: Icon, color, subtitle, trend }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-500 text-white',
    red: 'bg-red-500 text-white',
    orange: 'bg-orange-500 text-white',
    green: 'bg-green-500 text-white'
  };

  return (
    <div className="bg-white overflow-hidden shadow-lg rounded-lg border">
      <div className="p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <div>
              <div className="text-sm font-medium text-gray-500 truncate">
                {title}
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </div>
              {subtitle && (
                <div className="text-xs text-gray-600 mt-1">
                  {subtitle}
                </div>
              )}
              {trend && (
                <div className="mt-2 text-xs text-gray-500">
                  <span className="font-medium">{trend.value}</span> {trend.label}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MiniStatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'purple' | 'yellow' | 'green' | 'indigo' | 'emerald';
  suffix?: string;
  subtitle?: string;
}

function MiniStatCard({ title, value, icon: Icon, color, suffix = '', subtitle }: MiniStatCardProps) {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-100',
    purple: 'text-purple-600 bg-purple-100',
    yellow: 'text-yellow-600 bg-yellow-100',
    green: 'text-green-600 bg-green-100',
    indigo: 'text-indigo-600 bg-indigo-100',
    emerald: 'text-emerald-600 bg-emerald-100'
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow border overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">
            {title}
          </p>
          <p className="text-lg font-bold text-gray-900 truncate">
            {value.toLocaleString()}{suffix}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-600 mt-1 truncate">
              {subtitle}
            </p>
          )}
        </div>
        <div className={`flex-shrink-0 p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

interface ProcessingStepProps {
  label: string;
  value: number;
  percentage: number;
  color: string;
  subtitle?: string;
}

function ProcessingStep({ label, value, percentage, color, subtitle }: ProcessingStepProps) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div>
          <span className="text-sm font-medium text-gray-700">{label}</span>
          {subtitle && (
            <p className="text-xs text-gray-600">{subtitle}</p>
          )}
        </div>
        <span className="text-sm font-bold text-gray-900">{Number(value).toLocaleString()}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all duration-300"
          style={{
            width: `${Math.min(percentage, 100)}%`,
            backgroundColor: color
          }}
        />
      </div>
      <div className="text-xs text-gray-500">
        {percentage.toFixed(1)}% of total
      </div>
    </div>
  );
}


interface QuickStatProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'red' | 'green' | 'purple';
}

function QuickStat({ label, value, icon: Icon, color }: QuickStatProps) {
  const colorClasses = {
    blue: 'text-blue-600',
    red: 'text-red-600',
    green: 'text-green-600',
    purple: 'text-purple-600'
  };

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center space-x-3">
        <Icon className={`h-4 w-4 ${colorClasses[color]}`} />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
      <span className="text-sm font-bold text-gray-900">{value}</span>
    </div>
  );
}